import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { FooterData, Page, Component } from '../ComponentEditModal.types';
import { PageSelector } from '../shared/PageSelector';
import { ColorCustomizationPanel } from '../shared/ColorInput';
import { getColorFieldsForComponent } from '../shared/colorFieldUtils';
import { useCustomColors } from '../../../contexts/CustomColorsContext';

interface FooterEditorProps {
  formData: FooterData;
  setFormData: (data: FooterData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const FooterEditor: React.FC<FooterEditorProps> = ({
  formData,
  setFormData,
  pages,
  isLoadingPages,
  currentPageComponents,
}) => {
  const data = formData;
  const themeColors = useCustomColors();

  const addLink = () => {
    const newLink = {
      text: 'New Link',
      url: '/',
      category: 'General',
    };
    setFormData({
      ...data,
      links: [...(data.links || []), newLink],
    });
  };

  const updateLink = (index: number, updates: Partial<FooterData['links'][0]>) => {
    const newLinks = [...(data.links || [])];
    newLinks[index] = { ...newLinks[index], ...updates };
    setFormData({ ...data, links: newLinks });
  };

  const removeLink = (index: number) => {
    const newLinks = (data.links || []).filter((_, i) => i !== index);
    setFormData({ ...data, links: newLinks });
  };

  const addSocialLink = () => {
    const newSocial = {
      platform: 'facebook',
      url: 'https://competepages.com',
    };
    setFormData({
      ...data,
      socialLinks: [...(data.socialLinks || []), newSocial],
    });
  };

  const updateSocialLink = (index: number, updates: Partial<FooterData['socialLinks'][0]>) => {
    const newSocials = [...(data.socialLinks || [])];
    newSocials[index] = { ...newSocials[index], ...updates };
    setFormData({ ...data, socialLinks: newSocials });
  };

  const removeSocialLink = (index: number) => {
    const newSocials = (data.socialLinks || []).filter((_, i) => i !== index);
    setFormData({ ...data, socialLinks: newSocials });
  };

  const updateCustomColor = (key: string, value: string | null) => {
    setFormData({
      ...data,
      customColors: { ...data.customColors, [key]: value },
    });
  };

  // Get theme-aware color defaults
  const colorFields = getColorFieldsForComponent('footer', themeColors);

  return (
    <div className="space-y-6">
      {/* Site Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Site Name *</label>
        <input
          type="text"
          value={data.siteName || ''}
          onChange={e => setFormData({ ...data, siteName: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Enter site name"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description (optional)
        </label>
        <textarea
          value={data.description || ''}
          onChange={e => setFormData({ ...data, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          rows={3}
          placeholder="Enter site description (press Enter for line break)"
        />
        <p className="text-xs text-gray-500 mt-1">Press Enter to add a line break</p>
      </div>

      {/* Copyright */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Copyright Text (optional)
        </label>
        <input
          type="text"
          value={data.copyright || ''}
          onChange={e => setFormData({ ...data, copyright: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          placeholder="Leave empty for auto-generated copyright"
        />
      </div>

      {/* Footer Links */}
      <FooterLinksSection
        links={data.links || []}
        pages={pages}
        isLoadingPages={isLoadingPages}
        currentPageComponents={currentPageComponents}
        onAddLink={addLink}
        onUpdateLink={updateLink}
        onRemoveLink={removeLink}
      />

      {/* Social Links */}
      <SocialLinksSection
        socialLinks={data.socialLinks || []}
        onAddSocialLink={addSocialLink}
        onUpdateSocialLink={updateSocialLink}
        onRemoveSocialLink={removeSocialLink}
      />

      {/* Color Customization */}
      <ColorCustomizationPanel
        title="Colors (Optional)"
        description="Customize footer colors. Leave empty to use defaults."
        customColors={data.customColors as Record<string, string | undefined>}
        colorFields={colorFields}
        onUpdateColor={updateCustomColor}
        onClearAllColors={() => setFormData({ ...data, customColors: undefined })}
      />
    </div>
  );
};

// Sub-components

interface FooterLinksSectionProps {
  links: FooterData['links'];
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
  onAddLink: () => void;
  onUpdateLink: (index: number, updates: Partial<FooterData['links'][0]>) => void;
  onRemoveLink: (index: number) => void;
}

const FooterLinksSection: React.FC<FooterLinksSectionProps> = ({
  links,
  pages,
  isLoadingPages,
  currentPageComponents,
  onAddLink,
  onUpdateLink,
  onRemoveLink,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Footer Links</label>

    <div className="space-y-3">
      {(links || []).length === 0 && (
        <p className="text-sm text-gray-400 italic">No footer links yet</p>
      )}
      {(links || []).map((link, index) => (
        <div key={index} className="flex space-x-2 items-center">
          <input
            type="text"
            value={link.text}
            onChange={e => onUpdateLink(index, { text: e.target.value })}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Link text"
          />
          <PageSelector
            value={link.url}
            onChange={url => onUpdateLink(index, { url })}
            pages={pages}
            isLoadingPages={isLoadingPages}
            currentPageComponents={currentPageComponents}
          />
          <input
            type="text"
            value={link.category || ''}
            onChange={e => onUpdateLink(index, { category: e.target.value })}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Category (optional)"
          />
          <button
            type="button"
            onClick={() => onRemoveLink(index)}
            className="p-2 text-red-600 hover:text-red-700"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>

    <button
      type="button"
      onClick={onAddLink}
      className={`${(links || []).length > 0 ? 'mt-3' : 'mt-1'} w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center space-x-1`}
    >
      <Plus size={16} />
      <span>Add Link</span>
    </button>
  </div>
);

interface SocialLinksSectionProps {
  socialLinks: FooterData['socialLinks'];
  onAddSocialLink: () => void;
  onUpdateSocialLink: (index: number, updates: Partial<FooterData['socialLinks'][0]>) => void;
  onRemoveSocialLink: (index: number) => void;
}

const SOCIAL_PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'github', label: 'GitHub' },
  { value: 'youtube', label: 'YouTube' },
];

const SocialLinksSection: React.FC<SocialLinksSectionProps> = ({
  socialLinks,
  onAddSocialLink,
  onUpdateSocialLink,
  onRemoveSocialLink,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Social Links</label>

    <div className="space-y-3">
      {(socialLinks || []).length === 0 && (
        <p className="text-sm text-gray-400 italic">No social links yet</p>
      )}
      {(socialLinks || []).map((social, index) => (
        <div key={index} className="flex space-x-2 items-center">
          <select
            value={social.platform}
            onChange={e => onUpdateSocialLink(index, { platform: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          >
            {SOCIAL_PLATFORMS.map(platform => (
              <option key={platform.value} value={platform.value}>
                {platform.label}
              </option>
            ))}
          </select>
          <input
            type="url"
            value={social.url}
            onChange={e => onUpdateSocialLink(index, { url: e.target.value })}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            placeholder="Social media URL"
          />
          <button
            type="button"
            onClick={() => onRemoveSocialLink(index)}
            className="p-2 text-red-600 hover:text-red-700"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>

    <button
      type="button"
      onClick={onAddSocialLink}
      className={`${(socialLinks || []).length > 0 ? 'mt-3' : 'mt-1'} w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-primary-400 hover:text-primary-600 transition-colors flex items-center justify-center space-x-1`}
    >
      <Plus size={16} />
      <span>Add Social Link</span>
    </button>
  </div>
);
